// Store purchases (ECONOMY.md §2): birds, the vet visit, grid expansions,
// consumables, and equipment that installs into the yard on purchase.
//
// Buying a waterer puts it to work immediately: it goes into the first free
// spot of its kind (sim/spots.ts), and the player can move it from there.

import { createChick, createGrid, newId } from '../core/initialState';
import { Rng } from '../core/rng';
import type { Breed, GameState, Structure } from '../core/types';
import { breedProfile } from './config';
import { getItem, initialStructureState, isUnlocked } from './items';
import { seasonPrice } from './season';
import { ensureSpots, flockCap, setFixture, type FixtureChoice } from './spots';

export type PurchaseResult =
  | { ok: true; message: 'bought' | 'bird_added' | 'vet' }
  | { ok: false; reason: 'unknown_item' | 'locked' | 'too_poor' | 'no_effect' | 'coop_full' };

// Everything the player can buy as a random common chick. Silkie is absent on
// purpose: REFOCUS §5 has it at about 2x a layer breed, so it sells through its
// own store item rather than the common pool. Buff Orpington is here because it
// is a launch breed (REFOCUS §6) — a Puerto Rican player has to be able to buy
// the bird that cannot take their summer.
const COMMON_BREEDS: Breed[] = [
  'leghorn',
  'rhode_island_red',
  'australorp',
  'isa_brown',
  'brahma',
  'plymouth_rock',
  'buff_orpington',
];

/** Vet visit: an Eggit sink that shortcuts a recovery the player could do free. */
export const VET_RECOVERY_HOURS = 6;

export function purchase(state: GameState, itemId: string, rng = new Rng(state.seed)): PurchaseResult {
  const item = getItem(itemId);
  if (!item) return { ok: false, reason: 'unknown_item' };
  if (!isUnlocked(item.gate, state)) return { ok: false, reason: 'locked' };
  // The season's price, not the catalogue's (REFOCUS §11b). The catalogue is the
  // fallback so a season that does not price an item still sells it. Every
  // debit below reads `price`, because a route that quietly charges a different
  // number is the bug this replaced.
  const price = seasonPrice(itemId, item.price);
  if (state.eggits < price) return { ok: false, reason: 'too_poor' };

  const effects = item.effects;

  if (effects.spawnChick) {
    if (state.flock.filter((c) => c.alive).length >= flockCap(state)) {
      return { ok: false, reason: 'coop_full' };
    }
    state.eggits -= price;
    const breed = COMMON_BREEDS[rng.int(0, COMMON_BREEDS.length - 1)];
    const chick = createChick(
      rng,
      breed,
      chickName(rng),
      spawnSpot(),
      14,
      'hen',
      Math.floor(state.simHour / 24),
    );
    state.flock.push(chick);
    return { ok: true, message: 'bird_added' };
  }

  if (effects.vetVisit) {
    const sick = state.flock.filter((c) => c.conditions.length > 0);
    if (sick.length === 0) return { ok: false, reason: 'no_effect' };
    state.eggits -= price;
    for (const bird of sick) {
      for (const condition of bird.conditions) {
        condition.treatedUntilHour = state.simHour + VET_RECOVERY_HOURS;
        condition.acknowledged = true;
      }
    }
    return { ok: true, message: 'vet' };
  }

  if (effects.fixture) {
    // A roof or a vent is a change to the coop, not something to store.
    const result = setFixture(state, {
      slot: effects.fixture.slot,
      value: effects.fixture.value,
    } as FixtureChoice);
    return result.ok ? { ok: true, message: 'bought' } : { ok: false, reason: 'too_poor' };
  }

  state.eggits -= price;
  if (item.installed) {
    installStructure(state, itemId, rng);
    return { ok: true, message: 'bought' };
  }
  // Consumables stock the inventory: feed bags, bedding, storm supplies.
  state.inventory[itemId] = (state.inventory[itemId] ?? 0) + 1;
  return { ok: true, message: 'bought' };
}

/** Adds an owned piece of equipment to the yard, fresh from the store. */
export function installStructure(state: GameState, itemId: string, rng: Rng): Structure {
  const item = getItem(itemId)!;
  const structure: Structure = {
    id: newId('str', rng),
    itemId,
    state: initialStructureState(item),
  };
  state.structures.push(structure);
  ensureSpots(state);
  return structure;
}

/** Buys into the inventory. Returns false when the player can't afford it. */
export function buyItem(state: GameState, itemId: string, quantity = 1): boolean {
  const item = getItem(itemId);
  if (!item) return false;
  const cost = seasonPrice(itemId, item.price) * quantity;
  if (state.eggits < cost) return false;
  state.eggits -= cost;
  state.inventory[itemId] = (state.inventory[itemId] ?? 0) + quantity;
  return true;
}

/** Dev/world fixture helper: grows the yard, carrying dirt and debris over. */
export function expandGrid(state: GameState, size: number): void {
  const terrain = state.housing === 'balcony' ? 'balcony_mat' : 'grass';
  const grown = createGrid(size, size, terrain);
  for (const tile of grown.tiles) {
    const existing = state.grid.tiles.find((t) => t.x === tile.x && t.y === tile.y);
    if (existing) {
      tile.dirtinessLevel = existing.dirtinessLevel;
      tile.debris = existing.debris;
    }
  }
  state.grid = grown;
}

function spawnSpot(): { x: number; y: number } {
  return { x: 2, y: 3 };
}

const CHICK_NAMES = ['Pinta', 'Rayo', 'Luna', 'Mota', 'Chispa', 'Sol', 'Nieve', 'Tinta'];

function chickName(rng: Rng): string {
  return CHICK_NAMES[rng.int(0, CHICK_NAMES.length - 1)];
}

/** Sale value of an egg by shell colour (ECONOMY.md §1). */
export { eggSalePrice } from './genetics';

/**
 * Rough monthly Eggit income, used by the dev dashboard's balance check:
 * ECONOMY.md §3 wants any single purchase to be ≤3 days of normal play.
 */
export function dailyIncomeEstimate(state: GameState): number {
  const layers = state.flock.filter((c) => c.stage === 'adult' && c.gender === 'hen');
  const eggsPerDay = layers.reduce(
    (sum, hen) => sum + breedProfile(hen.breed).eggRatePerWeek / 7,
    0,
  );
  // Daily checklist reward plus the eggs it produces. R1: matches CHECKLIST_REWARD=25.
  return Math.round(25 + eggsPerDay * 5);
}

export { newId };
